import { useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform, useReducedMotion } from 'framer-motion';
import { FiChevronRight, FiArrowUpRight } from 'react-icons/fi';
import api from '../utils/api';
import ProductCard, { KurtiPlaceholder } from '../components/ProductCard';
import {
  AuroraBlobs, GrainOverlay, GlassChip, SectionHeader,
  MagneticButton, fadeUp, stagger,
} from '../components/ui';

const CAT_COLORS = ['#7A1F2B', '#1E3A5F', '#5C1620', '#46524E', '#B08A3E'];

const U = (id, w = 800) => `https://images.unsplash.com/${id}?auto=format&fit=crop&w=${w}&q=80`;

// Hardcoded — always available immediately, no API wait.
const HERO_IMAGES = [
  U('photo-1717585679395-bbe39b5fb6bc', 600),
  U('photo-1503160865267-af4660ce7bf2', 600),
  U('photo-1739429942851-9083ee185d3d', 600),
];
// Fallback category labels shown before API resolves
const HERO_STUBS = [
  { name: 'Anarkali', hindi: 'अनारकली', slug: 'anarkali' },
  { name: 'Cotton',   hindi: 'सूती',    slug: 'cotton'   },
  { name: 'Printed',  hindi: 'छपा',     slug: 'printed'  },
];
const CATEGORY_IMAGES = [
  U('photo-1622207691293-5cd80466dab3'),
  U('photo-1610189012906-4c0aa9b9781e'),
  U('photo-1569810020669-aa9d38003ea7'),
  U('photo-1479812627010-aa5bd9d173b1'),
  U('photo-1773846012458-e6a66c26e49f'),
];
const EDITORIAL_IMAGE = U('photo-1768651925876-637f68cd64f6', 700);

const TRUST = [
  { t: 'COD ₹0 fee',       s: 'Pan-India',        i: '⊕' },
  { t: '7-day returns',    s: 'No questions',      i: '↺' },
  { t: 'Razorpay secure',  s: 'UPI · Card · EMI', i: '◈' },
  { t: 'WhatsApp support', s: '9 am – 9 pm',      i: '✦' },
];
const MARQUEE = [
  'Free shipping ₹999+',
  'Hand-block printed in Rajasthan',
  'Easy 7-day returns',
  'COD across India',
  '50,000+ kurtis delivered',
  'Crafted by women artisans',
];

export default function HomePage() {
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const heroRef = useRef(null);

  // Parallax — disabled on mobile (pointer: coarse) to avoid iOS jank
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 80]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.4]);

  const { data: categoriesData } = useQuery({ queryKey: ['categories'], queryFn: () => api.get('/categories') });
  const { data: newArrivalsData } = useQuery({ queryKey: ['new-arrivals'], queryFn: () => api.get('/products/featured/new-arrivals') });
  const { data: bestsellersData } = useQuery({ queryKey: ['bestsellers'], queryFn: () => api.get('/products/featured/bestsellers') });
  const { data: bannersData } = useQuery({ queryKey: ['banners-home'], queryFn: () => api.get('/banners?placement=Home hero') });

  const categories = categoriesData?.categories || [];
  const newArrivals = newArrivalsData?.products || [];
  const bestsellers = bestsellersData?.products || [];
  const heroBanner = bannersData?.banners?.[0];

  // Merge live category data into the 3 hero stubs as soon as it arrives.
  // This way cards always render immediately; labels just upgrade when data loads.
  const heroCards = HERO_STUBS.map((stub, i) => {
    const live = categories.find(c => c.slug === stub.slug);
    return live ? { ...live } : stub;
  });

  return (
    <div style={{ background: 'var(--bg)', overflow: 'hidden' }}>
      <HpStyles />

      {/* ──────────────────── HERO ──────────────────── */}
      <section
        ref={heroRef}
        className="hp-hero"
        style={{
          position: 'relative',
          /* 100dvh: correct on iOS (excludes browser chrome). Falls back to 100vh. */
          minHeight: 'min(100dvh, 100vh)',
          background: '#1A100E',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <AuroraBlobs />
        <GrainOverlay />

        <motion.div
          className="hp-hero-inner"
          style={{ position: 'relative', zIndex: 2, maxWidth: 1280, margin: '0 auto', padding: '96px 24px', width: '100%', y: heroY, opacity: heroOpacity }}
        >
          <div className="hp-hero-grid" style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: 64, alignItems: 'center' }}>

            {/* ─── Left: text ─── */}
            <motion.div variants={stagger} initial="hidden" animate="show">
              <motion.div variants={fadeUp}>
                <GlassChip>{heroBanner?.subtitle || 'वृति का त्यौहार'}</GlassChip>
              </motion.div>

              <motion.h1
                variants={fadeUp}
                className="serif hp-hero-title"
                style={{ fontSize: 'clamp(38px, 6.4vw, 88px)', lineHeight: 0.98, color: '#FFF', marginTop: 22, marginBottom: 22, fontWeight: 600, letterSpacing: '-0.02em' }}
              >
                Worn by{' '}
                <span style={{ position: 'relative', display: 'inline-block' }}>
                  <em style={{ background: 'linear-gradient(135deg, #E9C77A 0%, #B08A3E 50%, #7A5618 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontStyle: 'italic' }}>her story</em>
                  <motion.span
                    aria-hidden
                    style={{ position: 'absolute', left: 0, right: 0, bottom: -4, height: 2, background: 'linear-gradient(90deg, transparent, var(--gold), transparent)', transformOrigin: 'left' }}
                    initial={{ scaleX: 0 }} animate={{ scaleX: 1 }}
                    transition={{ duration: 1.2, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  />
                </span>
                <br />
                Crafted by <span style={{ fontStyle: 'italic' }}>her hands.</span>
              </motion.h1>

              <motion.p variants={fadeUp} className="hp-hero-body" style={{ fontSize: 15, lineHeight: 1.7, color: 'rgba(237,227,209,0.72)', maxWidth: 440, marginBottom: 32 }}>
                Hand-crafted kurtis from the looms of Rajasthan. Each piece carries the signature of its maker on the inside hem.
              </motion.p>

              <motion.div variants={fadeUp} style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <MagneticButton to="/category/anarkali" variant="gold">
                  Shop the edit <FiChevronRight />
                </MagneticButton>
                <MagneticButton to="/category/cotton" variant="glassDark">
                  Browse all
                </MagneticButton>
              </motion.div>

              <motion.div variants={fadeUp} className="hp-stats-row" style={{ display: 'flex', alignItems: 'center', gap: 24, marginTop: 48, paddingTop: 24, borderTop: '1px solid rgba(237,227,209,0.12)' }}>
                <Stat n="50K+" l="Kurtis delivered" />
                <Divider />
                <Stat n="4.8★" l="2,400 reviews" />
                <Divider />
                <Stat n="120+" l="Artisan partners" />
              </motion.div>
            </motion.div>

            {/* ─── Right: floating cards (hidden on mobile via CSS) ─── */}
            <motion.div
              className="hp-hero-cards"
              variants={stagger} initial="hidden" animate="show"
              style={{ display: 'flex', gap: 16, justifyContent: 'center', alignItems: 'flex-end' }}
            >
              {heroCards.map((cat, i) => (
                <FloatingCard
                  key={cat.slug || i}
                  cat={cat}
                  color={CAT_COLORS[i]}
                  image={HERO_IMAGES[i]}
                  index={i}
                  navigate={navigate}
                  reduce={reduce}
                />
              ))}
            </motion.div>
          </div>
        </motion.div>

        {/* Mobile single-image background overlay */}
        <div className="hp-mobile-bg" aria-hidden>
          <img
            src={HERO_IMAGES[0]}
            alt=""
            width="600"
            height="800"
            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top' }}
          />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(26,16,14,0.55) 0%, rgba(26,16,14,0.2) 50%, rgba(26,16,14,0.85) 100%)' }} />
        </div>

        {/* Scroll cue */}
        <motion.div
          className="hp-scroll-cue"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          transition={{ delay: 1.6 }}
          style={{ position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, color: 'rgba(237,227,209,0.5)', fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', zIndex: 2 }}
        >
          Scroll
          <motion.span
            animate={reduce ? {} : { y: [0, 6, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
            style={{ display: 'block', width: 1, height: 24, background: 'linear-gradient(to bottom, var(--gold), transparent)' }}
          />
        </motion.div>
      </section>

      {/* ──────────────────── MARQUEE ──────────────────── */}
      <div style={{ background: 'var(--ink)', color: '#FAF6EE', overflow: 'hidden', padding: '13px 0' }}>
        <motion.div
          animate={reduce ? {} : { x: ['0%', '-50%'] }}
          transition={{ duration: 32, repeat: Infinity, ease: 'linear' }}
          style={{ display: 'flex', gap: 0, whiteSpace: 'nowrap' }}
        >
          {[...MARQUEE, ...MARQUEE, ...MARQUEE, ...MARQUEE].map((m, i) => (
            <span key={i} style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.18em', textTransform: 'uppercase', display: 'inline-flex', alignItems: 'center', gap: 0, padding: '0 28px' }}>
              <span style={{ color: 'var(--gold)', marginRight: 12 }}>✦</span>{m}
            </span>
          ))}
        </motion.div>
      </div>

      {/* ──────────────────── CATEGORIES ──────────────────── */}
      <section style={{ maxWidth: 1280, margin: '0 auto', padding: 'clamp(40px, 8vw, 96px) 16px 32px' }}>
        <SectionHeader kicker="Shop by" title="Categories" hindi="श्रेणियाँ" href="/category/all" navigate={navigate} />
        <motion.div
          variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, margin: '-60px' }}
          className="hp-cat-grid"
          style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 14 }}
        >
          {categories.length === 0
            ? Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="ve-skeleton" style={{ aspectRatio: '3/4', borderRadius: 14 }} />
              ))
            : categories.slice(0, 5).map((cat, i) => (
                <CategoryTile key={cat._id} cat={cat} color={CAT_COLORS[i]} image={CATEGORY_IMAGES[i]} />
              ))}
        </motion.div>
      </section>

      {/* ──────────────────── NEW ARRIVALS ──────────────────── */}
      {newArrivals.length > 0 && (
        <section style={{ maxWidth: 1280, margin: '0 auto', padding: '32px 16px' }}>
          <SectionHeader kicker="Just landed" title="New Arrivals" hindi="नई आवक" href="/category/new" navigate={navigate} />
          <motion.div
            variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, margin: '-60px' }}
            className="hp-products-grid"
            style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}
          >
            {newArrivals.slice(0, 4).map(p => (
              <motion.div key={p._id} variants={fadeUp}><ProductCard product={p} /></motion.div>
            ))}
          </motion.div>
        </section>
      )}

      {/* ──────────────────── EDITORIAL SPLIT ──────────────────── */}
      <section style={{ maxWidth: 1280, margin: '32px auto', padding: '0 16px' }}>
        <motion.div
          initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="hp-editorial-card"
          style={{ position: 'relative', borderRadius: 24, overflow: 'hidden', background: 'linear-gradient(135deg, #1A100E 0%, #2A1F1A 60%, #3A1F1F 100%)' }}
        >
          <AuroraBlobs muted />
          <GrainOverlay />
          <div className="hp-editorial-grid" style={{ position: 'relative', zIndex: 2, display: 'grid', gridTemplateColumns: '1fr 1.1fr' }}>
            <div style={{ padding: 'clamp(32px, 5vw, 64px) clamp(24px, 5vw, 56px)', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <GlassChip>Hand-block · Bagru</GlassChip>
              <h2 className="serif" style={{ fontSize: 'clamp(26px, 4vw, 52px)', lineHeight: 1.1, color: '#FFF', marginTop: 18, marginBottom: 16, letterSpacing: '-0.01em' }}>
                From the looms of <em style={{ color: '#E9C77A' }}>Rajasthan.</em>
              </h2>
              <p style={{ fontSize: 14, color: 'rgba(237,227,209,0.7)', lineHeight: 1.8, marginBottom: 24, maxWidth: 400 }}>
                Indigo dyed, dried in the sun, then block-printed by hand. Each piece is a quiet collaboration.
              </p>
              <MagneticButton to="/category/cotton" variant="glassDark" style={{ alignSelf: 'flex-start' }}>
                The collection <FiArrowUpRight />
              </MagneticButton>
            </div>
            <div className="hp-editorial-img-col" style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', padding: 36 }}>
              <div style={{ width: '78%', maxWidth: 340, aspectRatio: '3/4', borderRadius: 16, overflow: 'hidden', boxShadow: '0 30px 60px rgba(0,0,0,0.5)', position: 'relative' }}>
                {/* Use a plain <img> here — motion img caused flicker on mobile */}
                <img
                  src={EDITORIAL_IMAGE}
                  alt="Hand-block printed kurti"
                  loading="lazy"
                  width="700"
                  height="933"
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  onError={e => { e.currentTarget.style.display = 'none'; }}
                />
                <KurtiPlaceholder color="#B08A3E" size="100%" />
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* ──────────────────── BESTSELLERS ──────────────────── */}
      {bestsellers.length > 0 && (
        <section style={{ maxWidth: 1280, margin: '0 auto', padding: '32px 16px' }}>
          <SectionHeader kicker="Most loved" title="Bestsellers" hindi="पसंदीदा" href="/category/all" navigate={navigate} />
          <motion.div
            variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, margin: '-60px' }}
            className="hp-products-grid"
            style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}
          >
            {bestsellers.slice(0, 4).map(p => (
              <motion.div key={p._id} variants={fadeUp}><ProductCard product={p} /></motion.div>
            ))}
          </motion.div>
        </section>
      )}

      {/* ──────────────────── TRUST GLASS GRID ──────────────────── */}
      <section style={{ maxWidth: 1280, margin: '0 auto', padding: '40px 16px' }}>
        <motion.div
          variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, margin: '-60px' }}
          className="hp-trust-grid"
          style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}
        >
          {TRUST.map(x => (
            <motion.div key={x.t} variants={fadeUp} whileHover={{ y: -4 }} transition={{ duration: 0.2 }}
              style={{
                padding: '24px 18px', textAlign: 'center',
                background: 'rgba(255,255,255,0.55)',
                border: '1px solid rgba(229,219,200,0.6)',
                borderRadius: 18,
                backdropFilter: 'blur(20px) saturate(160%)',
                WebkitBackdropFilter: 'blur(20px) saturate(160%)',
                boxShadow: '0 8px 24px rgba(42,31,26,0.05)',
              }}>
              <div style={{ fontSize: 24, color: 'var(--gold)', marginBottom: 10, lineHeight: 1 }}>{x.i}</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)', marginBottom: 4 }}>{x.t}</div>
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>{x.s}</div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* ──────────────────── FINAL CTA ──────────────────── */}
      <section style={{ maxWidth: 1280, margin: '0 auto', padding: '16px 16px clamp(48px,8vw,96px)' }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }} whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          style={{ position: 'relative', borderRadius: 24, overflow: 'hidden', padding: 'clamp(40px,6vw,72px) clamp(20px,4vw,48px)', textAlign: 'center', background: 'linear-gradient(135deg, var(--primary) 0%, #5C1620 100%)' }}
        >
          <AuroraBlobs muted />
          <GrainOverlay />
          <div style={{ position: 'relative', zIndex: 2 }}>
            <div className="hindi" style={{ fontSize: 15, color: 'rgba(255,255,255,0.65)', marginBottom: 12 }}>आपकी कहानी, हमारी कारीगरी</div>
            <h2 className="serif" style={{ fontSize: 'clamp(28px, 4.4vw, 56px)', color: '#FFF', lineHeight: 1.1, marginBottom: 14, letterSpacing: '-0.02em' }}>
              First order on us.
            </h2>
            <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.75)', marginBottom: 24, maxWidth: 460, marginLeft: 'auto', marginRight: 'auto' }}>
              Get 10% off your first VritiEthnic piece with code{' '}
              <strong style={{ color: 'var(--gold)', letterSpacing: '0.08em' }}>VRITI10</strong>.
            </p>
            <Link to="/category/all" className="btn btn-gold btn-lg" style={{ boxShadow: '0 10px 32px rgba(176,138,62,0.3)' }}>
              Start shopping <FiChevronRight />
            </Link>
          </div>
        </motion.div>
      </section>
    </div>
  );
}

// ──────────── Sub-components ────────────

function FloatingCard({ cat, color, image, index, navigate, reduce }) {
  const heights = [400, 340, 300];
  const widths  = [190, 168, 148];
  return (
    <motion.div
      variants={fadeUp}
      onClick={() => cat.slug && navigate(`/category/${cat.slug}`)}
      whileHover={{ y: -12, transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] } }}
      animate={reduce ? {} : { y: [0, index === 1 ? -7 : 5, 0] }}
      transition={{ duration: 5 + index, repeat: Infinity, ease: 'easeInOut' }}
      style={{
        width: widths[index], height: heights[index],
        borderRadius: 16, overflow: 'hidden',
        cursor: cat.slug ? 'pointer' : 'default',
        position: 'relative', flexShrink: 0,
        boxShadow: '0 24px 56px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.06) inset',
      }}
    >
      {/* img with eager loading — these are above the fold */}
      <img
        src={image}
        alt={cat.name}
        loading="eager"
        fetchpriority="high"
        width="600" height="800"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        onError={e => { e.currentTarget.style.display = 'none'; }}
      />
      {/* Fallback SVG sits underneath */}
      <KurtiPlaceholder color={color} size="100%" />
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.6) 100%)' }} />
      <div style={{ position: 'absolute', bottom: 14, left: 14, right: 14, color: '#FFF' }}>
        {cat.hindi && <div className="hindi" style={{ fontSize: 10, opacity: 0.85, marginBottom: 2 }}>{cat.hindi}</div>}
        <div className="serif" style={{ fontSize: 18, lineHeight: 1 }}>{cat.name}</div>
      </div>
    </motion.div>
  );
}

function CategoryTile({ cat, color, image }) {
  return (
    <motion.div variants={fadeUp}>
      <Link to={`/category/${cat.slug}`} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
        <motion.div
          whileHover={{ y: -6, scale: 1.02, transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] } }}
          style={{ borderRadius: 14, overflow: 'hidden', position: 'relative', aspectRatio: '3/4', boxShadow: '0 8px 24px rgba(42,31,26,0.1)' }}
        >
          <img
            src={image}
            alt={cat.name}
            loading="lazy"
            width="800" height="1067"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            onError={e => { e.currentTarget.style.display = 'none'; }}
          />
          <KurtiPlaceholder color={color} size="100%" />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, transparent 45%, rgba(20,12,10,0.7) 100%)' }} />
          <motion.div whileHover={{ opacity: 1 }} initial={{ opacity: 0 }} style={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at 50% 100%, rgba(176,138,62,0.25), transparent 60%)' }} />
          <div style={{ position: 'absolute', bottom: 14, left: 14, right: 14, color: '#FFF' }}>
            {cat.hindi && <div className="hindi" style={{ fontSize: 11, opacity: 0.85 }}>{cat.hindi}</div>}
            <div className="serif" style={{ fontSize: 20, lineHeight: 1, marginTop: 2 }}>{cat.name}</div>
            {cat.tag && <div style={{ fontSize: 10, color: 'var(--gold)', marginTop: 5, letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 600 }}>{cat.tag}</div>}
          </div>
        </motion.div>
      </Link>
    </motion.div>
  );
}

function Stat({ n, l }) {
  return (
    <div>
      <div className="serif" style={{ fontSize: 20, color: '#FFF', lineHeight: 1, marginBottom: 2 }}>{n}</div>
      <div style={{ fontSize: 10, color: 'rgba(237,227,209,0.6)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>{l}</div>
    </div>
  );
}
function Divider() {
  return <div style={{ width: 1, height: 26, background: 'rgba(237,227,209,0.15)' }} />;
}

function HpStyles() {
  return (
    <style>{`
      /* ── Hero mobile background ──────────────── */
      .hp-mobile-bg {
        display: none;
        position: absolute; inset: 0;
        z-index: 1;
      }

      /* ── Responsive breakpoints ─────────────── */
      @media (max-width: 860px) {
        /* Hero: single column, left content only */
        .hp-hero-grid       { grid-template-columns: 1fr !important; gap: 0 !important; }
        .hp-hero-cards      { display: none !important; }
        .hp-mobile-bg       { display: block !important; }
        .hp-hero-inner      { padding: 0 !important; }
        .hp-hero            { min-height: min(100dvh, 100vh) !important; }

        /* Move inner content above the mobile bg */
        .hp-hero > div[style] { z-index: 3; }

        /* Hero text: sits on top of the bg image */
        .hp-hero-grid > div:first-child {
          padding: 100px 20px 56px !important;
          position: relative; z-index: 3;
        }
        .hp-hero-title   { font-size: clamp(34px, 9vw, 56px) !important; }
        .hp-hero-body    { font-size: 14px !important; }
        .hp-stats-row    { gap: 16px !important; margin-top: 32px !important; }
        .hp-scroll-cue   { display: none !important; }

        /* Category grid: 2 cols */
        .hp-cat-grid         { grid-template-columns: repeat(2, 1fr) !important; gap: 10px !important; }
        /* Products: 2 cols */
        .hp-products-grid    { grid-template-columns: repeat(2, 1fr) !important; gap: 12px !important; }
        /* Trust: 2 cols */
        .hp-trust-grid       { grid-template-columns: repeat(2, 1fr) !important; gap: 10px !important; }
        /* Editorial: stack */
        .hp-editorial-grid   { grid-template-columns: 1fr !important; }
        .hp-editorial-img-col { display: none !important; }
        .hp-editorial-card   { border-radius: 18px !important; }
      }

      @media (max-width: 480px) {
        /* Very small phones: 1-col categories */
        .hp-cat-grid      { grid-template-columns: 1fr 1fr !important; }
        .hp-products-grid { grid-template-columns: 1fr 1fr !important; }
      }
    `}</style>
  );
}
